import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Building2, Mail, Phone, FileText, Calendar, Clock, 
  ChevronRight, Check, AlertCircle, Send, Eye, Download,
  DollarSign, Briefcase, MapPin, User, ArrowLeft, Plus,
  CheckCircle2, XCircle, Timer, Award, Shield, Ban, Settings,
  ExternalLink, Lock, Unlock, Camera, Upload, Image, Paperclip,
  Navigation, Loader2, CheckCircle, X, Smartphone, ClipboardList,
  FileSignature
} from 'lucide-react';
import ContractorPunchList from '../components/ContractorPunchList';
import ContractorAgreement from '../components/ContractorAgreement';
import ContractorQualityChecklist from '../components/ContractorQualityChecklist';
import { drawingImageProps } from '@/utils/drawingImage';
import DrawingCard from '@/components/DrawingCard';
import GlobalFooter from '../components/GlobalFooter';
import ProjectHeader from '../components/ProjectHeader';
import HelpIcon from '../components/HelpIcon';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================================
// Phase 7.3 — Contractor Session axios instance
// ============================================================
// All `/api/contractor-portal/*` calls (except the 2 issuance endpoints
// `/login` and `/verify-token`, plus the anonymous RFP-bid surface
// `/{access_code}/submit-bid` and `/rfp/{rfp_id}/...`) go through this
// dedicated axios instance. The interceptor reads
// `contractor_session_token` from sessionStorage and attaches it as
// `X-Contractor-Session` on every request. On 401 the token is cleared
// — the route component reacts to that by sending the user back to the
// magic-link / login screen.
//
// `sessionStorage` (not `localStorage`) intentionally: the 4h JWT
// matches the "until tab closes" lifetime, and we don't want a stale
// token surviving across browser sessions.
export const CONTRACTOR_SESSION_KEY = 'contractor_session_token';

export const contractorAxios = axios.create();

contractorAxios.interceptors.request.use((config) => {
  const tok = sessionStorage.getItem(CONTRACTOR_SESSION_KEY);
  if (tok) {
    config.headers = config.headers || {};
    config.headers['X-Contractor-Session'] = tok;
  }
  return config;
});

contractorAxios.interceptors.response.use(
  (res) => res,
  (err) => {
    // 401 from any session-protected endpoint → the token is gone or
    // expired. Clear it so the UI can detect the missing session and
    // route the user back to login.
    if (err.response && err.response.status === 401) {
      sessionStorage.removeItem(CONTRACTOR_SESSION_KEY);
    }
    return Promise.reject(err);
  }
);

// ============================================
// GANTT CHART COMPONENT WITH DEPENDENCIES
// ============================================
const GanttChart = ({ tasks, onTaskUpdate, onAddDependency, onRemoveDependency }) => {
  const [ganttZoom, setGanttZoom] = useState('month');
  const [dragInfo, setDragInfo] = useState(null);
  const [linkMode, setLinkMode] = useState(null);
  const [showDependencies, setShowDependencies] = useState(true);
  
  // Filter tasks with due dates
  const tasksWithDates = tasks.filter(t => t.due_date);
  
  // Build dependency map
  const dependencyMap = {};
  tasksWithDates.forEach(task => {
    if (task.dependencies && task.dependencies.length > 0) {
      task.dependencies.forEach(depId => {
        if (!dependencyMap[depId]) dependencyMap[depId] = [];
        dependencyMap[depId].push(task.id);
      });
    }
  });
  
  if (tasksWithDates.length === 0) {
    return (
      <div className="text-center py-12">
        <Calendar className="w-12 h-12 text-[#3A3A3A] mx-auto mb-4" />
        <p className="text-slate-500 dark:text-[#8A8A8A]">No scheduled tasks</p>
        <p className="text-sm text-slate-400 dark:text-[#5A5A5A]">Tasks with due dates will appear on the timeline</p>
      </div>
    );
  }
  
  // Calculate date range
  const dates = tasksWithDates.map(t => new Date(t.due_date));
  const minDate = new Date(Math.min(...dates));
  const maxDate = new Date(Math.max(...dates));
  
  // Expand range for visualization
  const rangeStart = new Date(minDate);
  rangeStart.setDate(rangeStart.getDate() - 7);
  const rangeEnd = new Date(maxDate);
  rangeEnd.setDate(rangeEnd.getDate() + 14);
  
  // Generate day columns based on zoom
  const generateDays = () => {
    const days = [];
    const current = new Date(rangeStart);
    while (current <= rangeEnd) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return days;
  };
  
  const days = generateDays();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Calculate bar position and width
  const getTaskBar = (task) => {
    const taskDate = new Date(task.due_date);
    taskDate.setHours(0, 0, 0, 0);
    
    // Assume task duration is 1-5 days based on priority
    const durationDays = task.priority === 'high' ? 1 : task.priority === 'medium' ? 3 : 5;
    const startDate = new Date(taskDate);
    startDate.setDate(startDate.getDate() - durationDays);
    
    const dayWidth = ganttZoom === 'week' ? 40 : ganttZoom === 'month' ? 20 : 12;
    const totalDays = Math.ceil((rangeEnd - rangeStart) / (1000 * 60 * 60 * 24));
    
    const startOffset = Math.max(0, Math.ceil((startDate - rangeStart) / (1000 * 60 * 60 * 24)));
    const barWidth = durationDays * dayWidth;
    
    return {
      left: startOffset * dayWidth,
      width: barWidth,
      isOverdue: taskDate < today,
      isPast: taskDate < today,
      isConfirmed: task.contractor_response === 'confirmed',
      isDeclined: task.contractor_response === 'declined'
    };
  };
  
  // Handle drag to change date
  const handleMouseDown = (e, task) => {
    const rect = e.target.getBoundingClientRect();
    setDragInfo({
      taskId: task.id,
      startX: e.clientX,
      originalDate: new Date(task.due_date)
    });
  };
  
  const handleMouseMove = (e) => {
    if (!dragInfo) return;
    
    const dayWidth = ganttZoom === 'week' ? 40 : ganttZoom === 'month' ? 20 : 12;
    const deltaX = e.clientX - dragInfo.startX;
    const deltaDays = Math.round(deltaX / dayWidth);
    
    const newDate = new Date(dragInfo.originalDate);
    newDate.setDate(newDate.getDate() + deltaDays);
    
    // Update visually
    setDragInfo(prev => ({ ...prev, currentOffset: deltaDays }));
  };
  
  const handleMouseUp = async () => {
    if (!dragInfo || dragInfo.currentOffset === undefined) {
      setDragInfo(null);
      return;
    }
    
    const deltaDays = dragInfo.currentOffset;
    const newDate = new Date(dragInfo.originalDate);
    newDate.setDate(newDate.getDate() + deltaDays);
    
    // Cascade to dependent tasks if moving forward
    const cascadeUpdates = [];
    if (deltaDays > 0) {
      const findDependents = (taskId, visited = new Set()) => {
        if (visited.has(taskId)) return;
        visited.add(taskId);
        const dependents = dependencyMap[taskId] || [];
        dependents.forEach(depTaskId => {
          const depTask = tasksWithDates.find(t => t.id === depTaskId);
          if (depTask) {
            const depDate = new Date(depTask.due_date);
            depDate.setDate(depDate.getDate() + deltaDays);
            cascadeUpdates.push({ taskId: depTaskId, newDate: depDate.toISOString() });
            findDependents(depTaskId, visited);
          }
        });
      };
      findDependents(dragInfo.taskId);
    }
    
    await onTaskUpdate(dragInfo.taskId, newDate.toISOString(), cascadeUpdates);
    setDragInfo(null);
  };
  
  // Handle link mode for creating dependencies
  const handleLinkClick = (taskId) => {
    if (!linkMode) {
      setLinkMode({ fromTaskId: taskId });
    } else if (linkMode.fromTaskId !== taskId) {
      if (onAddDependency) {
        onAddDependency(taskId, linkMode.fromTaskId);
      }
      setLinkMode(null);
    } else {
      setLinkMode(null);
    }
  };
  
  const dayWidth = ganttZoom === 'week' ? 40 : ganttZoom === 'month' ? 20 : 12;
  
  return (
    <div 
      className="space-y-4"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={() => setDragInfo(null)}
    >
      {/* Controls */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 dark:text-[#8A8A8A]">Zoom:</span>
          <div className="flex bg-white dark:bg-[#0D0D0D] rounded-lg p-1">
            <button
              className={`px-3 py-1 text-xs rounded transition-colors ${ganttZoom === 'week' ? 'bg-[#70170F] text-white' : 'text-slate-500 dark:text-[#8A8A8A] hover:text-white'}`}
              onClick={() => setGanttZoom('week')}
            >
              Week
            </button>
            <button
              className={`px-3 py-1 text-xs rounded transition-colors ${ganttZoom === 'month' ? 'bg-[#70170F] text-white' : 'text-slate-500 dark:text-[#8A8A8A] hover:text-white'}`}
              onClick={() => setGanttZoom('month')}
            >
              Month
            </button>
            <button
              className={`px-3 py-1 text-xs rounded transition-colors ${ganttZoom === 'project' ? 'bg-[#70170F] text-white' : 'text-slate-500 dark:text-[#8A8A8A] hover:text-white'}`}
              onClick={() => setGanttZoom('project')}
            >
              Full
            </button>
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-green-500/50"></div>
            <span className="text-slate-500 dark:text-[#8A8A8A]">Confirmed</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-blue-500/50"></div>
            <span className="text-slate-500 dark:text-[#8A8A8A]">Pending</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-red-500/50"></div>
            <span className="text-slate-500 dark:text-[#8A8A8A]">Overdue</span>
          </div>
        </div>
      </div>
      
      {/* Timeline Container */}
      <div className="relative overflow-x-auto pb-4" style={{ maxHeight: '400px' }}>
        <div style={{ minWidth: days.length * dayWidth + 200 }}>
          {/* Header with dates */}
          <div className="flex border-b border-slate-200 dark:border-[#2A2A2A] sticky top-0 bg-slate-50 dark:bg-[#1A1A1A] z-10">
            <div className="w-[200px] flex-shrink-0 p-2 text-xs font-medium text-slate-500 dark:text-[#8A8A8A] border-r border-slate-200 dark:border-[#2A2A2A]">
              Task
            </div>
            <div className="flex">
              {days.map((day, i) => {
                const isToday = day.toDateString() === today.toDateString();
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                const showMonth = i === 0 || day.getDate() === 1;
                
                return (
                  <div 
                    key={i}
                    className={`flex-shrink-0 text-center border-r border-slate-200 dark:border-[#2A2A2A]/30 ${isToday ? 'bg-[#70170F]/30' : isWeekend ? 'bg-white dark:bg-[#0D0D0D]' : ''}`}
                    style={{ width: dayWidth }}
                  >
                    {showMonth && (
                      <div className="text-[10px] text-[#BC5D41] font-medium">
                        {day.toLocaleDateString('en-US', { month: 'short' })}
                      </div>
                    )}
                    <div className={`text-[10px] ${isToday ? 'text-[#BC5D41] font-bold' : 'text-slate-400 dark:text-[#5A5A5A]'}`}>
                      {day.getDate()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Task rows */}
          {tasksWithDates.map((task, index) => {
            const bar = getTaskBar(task);
            let dragOffset = 0;
            if (dragInfo && dragInfo.taskId === task.id && dragInfo.currentOffset) {
              dragOffset = dragInfo.currentOffset * dayWidth;
            }
            
            return (
              <div key={task.id} className="flex border-b border-slate-200 dark:border-[#2A2A2A]/30 hover:bg-white dark:bg-[#0D0D0D]/50">
                <div className="w-[200px] flex-shrink-0 p-2 border-r border-slate-200 dark:border-[#2A2A2A] overflow-hidden">
                  <div className="text-xs font-medium text-slate-800 dark:text-[#E5E2DD] truncate" title={task.title}>
                    {task.title}
                  </div>
                  <div className="text-[10px] text-slate-400 dark:text-[#5A5A5A] truncate">
                    {task.project_name || 'No project'}
                  </div>
                </div>
                <div className="relative flex-1" style={{ height: 40 }}>
                  {/* Today line */}
                  {days.findIndex(d => d.toDateString() === today.toDateString()) >= 0 && (
                    <div 
                      className="absolute top-0 bottom-0 w-px bg-[#70170F] z-10"
                      style={{ 
                        left: days.findIndex(d => d.toDateString() === today.toDateString()) * dayWidth + dayWidth / 2 
                      }}
                    />
                  )}
                  
                  {/* Task bar */}
                  <div
                    className={`absolute top-2 h-6 rounded cursor-ew-resize transition-colors flex items-center px-2 text-[10px] text-white font-medium overflow-hidden ${
                      bar.isDeclined 
                        ? 'bg-red-900/50 border border-red-700' 
                        : bar.isOverdue 
                        ? 'bg-red-500/60 border border-red-400' 
                        : bar.isConfirmed 
                        ? 'bg-green-500/50 border border-green-400 hover:bg-green-500/70' 
                        : 'bg-blue-500/50 border border-blue-400 hover:bg-blue-500/70'
                    }`}
                    style={{ 
                      left: bar.left + dragOffset, 
                      width: bar.width,
                      minWidth: 20
                    }}
                    onMouseDown={(e) => handleMouseDown(e, task)}
                    title={`Due: ${new Date(task.due_date).toLocaleDateString()}\n${task.contractor_response || 'Needs response'}`}
                  >
                    <span className="truncate">
                      {bar.isOverdue ? '!' : ''} {new Date(task.due_date).getDate()}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Instructions */}
      <p className="text-xs text-slate-400 dark:text-[#5A5A5A] text-center">
        Drag task bars left or right to adjust due dates
      </p>
    </div>
  );
};

export default function ContractorPortal() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Admin mode
  const isAdminView = searchParams.get('admin') === 'true';
  
  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  // PWA Install prompt
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  
  // Contractor data
  const [contractor, setContractor] = useState(null);
  const [rfps, setRfps] = useState([]);
  // Wave 5.9 — canonical membership-derived projects. Prefer this over
  // rfps[0].project_name for the header card + project listing.
  const [portalProjects, setPortalProjects] = useState([]);
  // Wave 5.9 — tenant company name for the portal header (Bug 3).
  const [tenantCompanyName, setTenantCompanyName] = useState('');
  const [summary, setSummary] = useState({ total: 0, pending: 0, awaiting_response: 0, submitted: 0, awarded: 0 });
  
  // RFP detail view
  const [selectedRfp, setSelectedRfp] = useState(null);
  const [rfpDetails, setRfpDetails] = useState(null);
  const [showRfpDetail, setShowRfpDetail] = useState(false);
  
  // Admin controls
  const [showAdminControls, setShowAdminControls] = useState(false);
  const [allContractors, setAllContractors] = useState([]);
  const [loadingContractors, setLoadingContractors] = useState(false);
  
  // Bid form
  const [showBidForm, setShowBidForm] = useState(false);
  const [bidForm, setBidForm] = useState({
    total_amount: '',
    labor_amount: '',
    materials_amount: '',
    notes: '',
    estimated_duration: '',
    availability_date: ''
  });
  const [isSubmittingBid, setIsSubmittingBid] = useState(false);
  
  // Portal tabs - default to null (show home/buttons view)
  const [activeTab, setActiveTab] = useState(null);
  // Wave 5.9 Build 10 — live badges for home nav buttons.
  const [portalBadges, setPortalBadges] = useState({ punch_list: 0, rfps: 0, plans: 0 });
  // Stage D — Plans tab state.
  const [plansSets, setPlansSets] = useState([]);
  const [openedPlansSet, setOpenedPlansSet] = useState(null); // {transmittal, sheets[]}
  const [loadingPlansSet, setLoadingPlansSet] = useState(false);
  const [plansSheetBlob, setPlansSheetBlob] = useState({ id: null, url: null, loading: false });
  
  // Assigned Tasks state
  const [assignedTasks, setAssignedTasks] = useState([]);
  const [showTaskDetailDialog, setShowTaskDetailDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  
  // Documents state
  const [documents, setDocuments] = useState([]);
  
  // Messages state
  const [messages, setMessages] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [newMessage, setNewMessage] = useState({ subject: '', message: '' });
  
  // Progress Log state
  const [progressLogs, setProgressLogs] = useState([]);
  const [showProgressLogForm, setShowProgressLogForm] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [progressLogForm, setProgressLogForm] = useState({
    project_id: '',
    notes: '',
    photos: [],
    attachments: [],
    gps_location: null
  });
  const [isCapturingLocation, setIsCapturingLocation] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  // Task date update handler for Gantt
  const handleTaskDateUpdate = async (taskId, newDueDate, cascadeUpdates = []) => {
    try {
      await axios.put(`${API}/tasks/${taskId}/due-date`, { due_date: newDueDate });
      
      // Update cascaded dependent tasks
      for (const update of cascadeUpdates) {
        await axios.put(`${API}/tasks/${update.taskId}/due-date`, { due_date: update.newDate });
      }
      
      // Update local state
      setAssignedTasks(prev => {
        let updated = prev.map(t => 
          t.id === taskId ? { ...t, due_date: newDueDate } : t
        );
        cascadeUpdates.forEach(update => {
          updated = updated.map(t => 
            t.id === update.taskId ? { ...t, due_date: update.newDate } : t
          );
        });
        return updated;
      });
      
      const msg = cascadeUpdates.length > 0 
        ? `Task updated + ${cascadeUpdates.length} dependent task(s) adjusted`
        : 'Task date updated';
      toast.success(msg);
    } catch (error) {
      console.error('Error updating task date:', error);
      toast.error('Failed to update task date');
    }
  };

  const handleAddDependency = async (taskId, predecessorId) => {
    try {
      await axios.post(`${API}/tasks/${taskId}/dependencies`, { predecessor_id: predecessorId });
      setAssignedTasks(prev => prev.map(t => {
        if (t.id === taskId) {
          const currentDeps = t.dependencies || [];
          if (!currentDeps.includes(predecessorId)) {
            return { ...t, dependencies: [...currentDeps, predecessorId] };
          }
        }
        return t;
      }));
      toast.success('Task dependency created');
    } catch (error) {
      console.error('Error adding dependency:', error);
      toast.error(error.response?.data?.detail || 'Failed to add dependency');
    }
  };

  const handleRemoveDependency = async (taskId, predecessorId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}/dependencies/${predecessorId}`);
      setAssignedTasks(prev => prev.map(t => {
        if (t.id === taskId) {
          return { ...t, dependencies: (t.dependencies || []).filter(d => d !== predecessorId) };
        }
        return t;
      }));
      toast.success('Dependency removed');
    } catch (error) {
      console.error('Error removing dependency:', error);
      toast.error('Failed to remove dependency');
    }
  };

  // Check for auto-login from URL params (email or magic link token)
  useEffect(() => {
    const token = searchParams.get('token');
    const email = searchParams.get('email');
    const isAdmin = searchParams.get('admin') === 'true';
    
    if (token) {
      // Magic link login
      verifyMagicToken(token);
    } else if (email) {
      setLoginEmail(email);
      handleLogin(email);
    } else if (isAdmin) {
      // Admin viewing without specific contractor - fetch all contractors for selection
      fetchAllContractorsForAdmin();
    }
  }, [searchParams]);
  
  // Verify magic link token
  const verifyMagicToken = async (token) => {
    // 2026-06-22 — setLoading()/setPortalSummary() did not exist on this page,
    // so the magic-link path threw a ReferenceError before it verified anything.
    // There is no page-level loading state to set here; the summary state is
    // `setSummary`.
    try {
      const response = await axios.post(`${API}/contractor-portal/verify-token`, { token });
      if (response.data.valid) {
        const contractorData = response.data.contractor;
        // Phase 7.3: persist the session token issued by /verify-token
        // BEFORE we make any subsequent contractorAxios calls.
        if (response.data.session_token) {
          sessionStorage.setItem(CONTRACTOR_SESSION_KEY, response.data.session_token);
        }
        setContractor(contractorData);
        setIsLoggedIn(true);

        // Also fetch their RFPs using the standard login flow
        const loginResponse = await axios.post(`${API}/contractor-portal/login`, { email: contractorData.email });
        // login also returns a fresh session_token — overwrite with the
        // most recent issuance so the timer restarts.
        if (loginResponse.data.session_token) {
          sessionStorage.setItem(CONTRACTOR_SESSION_KEY, loginResponse.data.session_token);
        }
        setRfps(loginResponse.data.rfps || []);
        setPortalProjects(loginResponse.data.projects || []);
        setTenantCompanyName(loginResponse.data.company_name || '');
        setSummary(loginResponse.data.summary);

        // Fetch assigned tasks (uses session — no email param needed)
        setTimeout(() => fetchAssignedTasks(), 500);

        toast.success(`Welcome back, ${contractorData.contact_name || contractorData.company_name}!`);
      }
    } catch (error) {
      console.error('Error verifying magic token:', error);
      toast.error(error.response?.data?.detail || 'Invalid or expired access link');
    }
  };
  
  // Fetch portal data when contractor logs in
  useEffect(() => {
    if (contractor?.id) {
      fetchDocuments();
      fetchMessages();
      fetchProgressLogs();
      // Wave 5.9 Build 10 — hydrate portal home badges.
      contractorAxios.get(`${API}/contractor-portal/badges`)
        .then((r) => setPortalBadges({ punch_list: 0, rfps: 0, plans: 0, ...(r.data || {}) }))
        .catch(() => {});
      // Stage D — hydrate transmitted sets for the Plans tab.
      contractorAxios.get(`${API}/contractor-portal/plans/sets`)
        .then((r) => setPlansSets(r.data?.sets || []))
        .catch(() => setPlansSets([]));
    }
  }, [contractor?.id]);
  
  // Capture PWA install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowInstallPrompt(false);
    }
    
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);
  
  // Show install prompt after successful login
  useEffect(() => {
    if (isLoggedIn && deferredInstallPrompt) {
      // Check if user has dismissed before
      const dismissed = localStorage.getItem('pwaInstallDismissed');
      if (!dismissed) {
        setTimeout(() => setShowInstallPrompt(true), 2000);
      }
    }
  }, [isLoggedIn, deferredInstallPrompt]);
  
  const handleInstallPWA = async () => {
    if (!deferredInstallPrompt) return;
    deferredInstallPrompt.prompt();
    const { outcome } = await deferredInstallPrompt.userChoice;
    if (outcome === 'accepted') {
      toast.success('App installed! Look for KEY-MADE on your home screen.');
    }
    setDeferredInstallPrompt(null);
    setShowInstallPrompt(false);
  };
  
  const dismissInstallPrompt = () => {
    setShowInstallPrompt(false);
    localStorage.setItem('pwaInstallDismissed', 'true');
  };
  
  const fetchDocuments = async () => {
    try {
      const res = await contractorAxios.get(`${API}/contractor-portal/${contractor.id}/documents`);
      setDocuments(res.data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };
  
  const fetchMessages = async () => {
    try {
      const res = await contractorAxios.get(`${API}/contractor-portal/${contractor.id}/messages`);
      setMessages(res.data.messages || []);
      setUnreadMessages(res.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };
  
  const fetchProgressLogs = async () => {
    try {
      const res = await contractorAxios.get(`${API}/contractor-portal/${contractor.id}/progress-logs`);
      setProgressLogs(res.data.progress_logs || []);
    } catch (error) {
      console.error('Error fetching progress logs:', error);
    }
  };

  // Fetch all contractors for admin selector
  const fetchAllContractorsForAdmin = async () => {
    setLoadingContractors(true);
    try {
      // 2026-07-14 — per-project portal-members rewire. If the URL
      // carries a `project_id` param, read the memberships-derived
      // list so the Manage-Contractor-Portal view matches Team +
      // Transmit chips exactly (ONE RULE). Fallback to tenant-wide
      // /api/contractors for cross-project management.
      const params = new URLSearchParams(window.location.search);
      const scopeProjectId = params.get('project_id') || params.get('projectId');
      if (scopeProjectId) {
        const res = await axios.get(`${API}/projects/${scopeProjectId}/portal-members`);
        const members = (res.data?.members || []).map((m) => ({
          id: m.contractor_id,
          company_name: m.company_name,
          contact_name: m.name,
          email: m.email,
          phone: m.phone,
          _membership_id: m.membership_id,
          _project_scoped: true,
        }));
        setAllContractors(members);
        return;
      }
      const res = await axios.get(`${API}/contractors`);
      setAllContractors(res.data || []);
    } catch (error) {
      console.error('Error fetching contractors:', error);
    } finally {
      setLoadingContractors(false);
    }
  };

  const handleLogin = async (email = loginEmail) => {
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    
    setIsLoggingIn(true);
    try {
      const res = await axios.post(`${API}/contractor-portal/login`, { email });
      // Phase 7.3: persist the session token before any contractorAxios
      // call fires.
      if (res.data.session_token) {
        sessionStorage.setItem(CONTRACTOR_SESSION_KEY, res.data.session_token);
      }
      setContractor(res.data.contractor);
      setRfps(res.data.rfps);
      setPortalProjects(res.data.projects || []);
      setTenantCompanyName(res.data.company_name || '');
      setSummary(res.data.summary);
      setIsLoggedIn(true);
      toast.success(`Welcome, ${res.data.contractor.company_name}!`);
      
      // Fetch assigned tasks after login
      setTimeout(() => fetchAssignedTasks(), 500);
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Login failed. Please check your email.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    // Phase 7.3: clear the session token on logout — otherwise the next
    // visitor on this tab inherits an active session.
    sessionStorage.removeItem(CONTRACTOR_SESSION_KEY);
    setIsLoggedIn(false);
    setContractor(null);
    setRfps([]);
    setAssignedTasks([]);
    setSummary({ total: 0, pending: 0, awaiting_response: 0, submitted: 0, awarded: 0 });
    setLoginEmail('');
  };

  const viewRfpDetails = async (rfp) => {
    setSelectedRfp(rfp);
    try {
      const res = await axios.get(`${API}/contractor-portal/rfp/${rfp.id}?contractor_id=${contractor.id}`);
      setRfpDetails(res.data);
      
      // Pre-fill bid form if existing bid
      if (res.data.existing_bid) {
        setBidForm({
          total_amount: res.data.existing_bid.total_amount || '',
          labor_amount: res.data.existing_bid.labor_amount || '',
          materials_amount: res.data.existing_bid.materials_amount || '',
          notes: res.data.existing_bid.notes || '',
          estimated_duration: res.data.existing_bid.estimated_duration || '',
          availability_date: res.data.existing_bid.availability_date || ''
        });
      } else {
        setBidForm({
          total_amount: '',
          labor_amount: '',
          materials_amount: '',
          notes: '',
          estimated_duration: '',
          availability_date: ''
        });
      }
      
      setShowRfpDetail(true);
    } catch (error) {
      console.error('Error fetching RFP details:', error);
      toast.error('Failed to load RFP details');
    }
  };

  // Fetch tasks assigned to contractor
  const fetchAssignedTasks = async () => {
    if (!contractor?.email) return;

    try {
      // Phase 7.3: server now derives identity from the session token.
      // The legacy `?contractor_email=` query param is gone.
      const response = await contractorAxios.get(`${API}/contractor-portal/tasks`);
      setAssignedTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  // Respond to task (confirm/decline)
  const handleTaskResponse = async (taskId, status, notes = '') => {
    try {
      await contractorAxios.post(`${API}/contractor-portal/tasks/${taskId}/respond`, {
        status,
        notes
      });
      
      toast.success(`Task ${status === 'confirmed' ? 'confirmed' : 'declined'} successfully`);
      setShowTaskDetailDialog(false);
      fetchAssignedTasks();
    } catch (error) {
      console.error('Error responding to task:', error);
      toast.error('Failed to respond to task');
    }
  };

  // Calculate days until due
  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const due = new Date(dueDate);
    const now = new Date();
    const diffTime = due - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleSubmitBid = async () => {
    if (!bidForm.total_amount) {
      toast.error('Please enter a bid amount');
      return;
    }
    
    setIsSubmittingBid(true);
    try {
      await axios.post(`${API}/contractor-portal/rfp/${selectedRfp.id}/submit-bid`, {
        contractor_id: contractor.id,
        ...bidForm,
        total_amount: parseFloat(bidForm.total_amount) || 0,
        labor_amount: parseFloat(bidForm.labor_amount) || 0,
        materials_amount: parseFloat(bidForm.materials_amount) || 0
      });
      
      toast.success('Bid submitted successfully!');
      setShowBidForm(false);
      setShowRfpDetail(false);
      
      // Refresh RFPs list
      handleLogin();
    } catch (error) {
      console.error('Error submitting bid:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit bid');
    } finally {
      setIsSubmittingBid(false);
    }
  };
  
  const handleSendMessage = async () => {
    if (!newMessage.message.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    try {
      await contractorAxios.post(`${API}/contractor-portal/${contractor.id}/messages`, {
        sender_type: 'contractor',
        sender_id: contractor.id,
        sender_name: contractor.company_name,
        subject: newMessage.subject,
        message: newMessage.message
      });
      
      toast.success('Message sent!');
      setNewMessage({ subject: '', message: '' });
      setShowMessageDialog(false);
      fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    }
  };
  
  // Capture GPS location
  const captureLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    
    setIsCapturingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setProgressLogForm(prev => ({
          ...prev,
          gps_location: {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: new Date().toISOString()
          }
        }));
        setIsCapturingLocation(false);
        toast.success('Location captured');
      },
      (error) => {
        console.error('Location error:', error);
        setIsCapturingLocation(false);
        toast.error('Unable to get location');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };
  
  // Handle photo upload from phone — auto-captures location and timestamp
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingPhotos(true);
    const uploadedPhotos = [];

    // Auto-capture location for this batch
    let location = progressLogForm.gps_location;
    if (!location && navigator.geolocation) {
      try {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 5000 });
        });
        location = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          timestamp: new Date().toISOString()
        };
      } catch (err) {
        console.log('Location not available:', err.message);
      }
    }
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', 'progress_log');
        
        const res = await axios.post(`${API}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (res.data.url) {
          uploadedPhotos.push({
            url: res.data.url,
            captured_at: new Date().toISOString(),
            gps_location: location,
            filename: file.name,
          });
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    // Store enriched photo data
    const photoUrls = uploadedPhotos.map(p => p.url);
    setProgressLogForm(prev => ({
      ...prev,
      photos: [...prev.photos, ...photoUrls],
      photo_metadata: [...(prev.photo_metadata || []), ...uploadedPhotos],
      gps_location: location || prev.gps_location,
    }));
    setUploadingPhotos(false);
    
    if (uploadedPhotos.length > 0) {
      toast.success(`${uploadedPhotos.length} photo(s) captured${location ? ' with location' : ''}`);
    }
  };
  
  // Handle file attachment upload
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', 'progress_log');
        
        const res = await axios.post(`${API}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (res.data.url) {
          setProgressLogForm(prev => ({
            ...prev,
            attachments: [...prev.attachments, { name: file.name, url: res.data.url }]
          }));
          toast.success(`${file.name} uploaded`);
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }
  };
  
  const handleSubmitProgressLog = async () => {
    if (!progressLogForm.project_id && rfps.length > 0) {
      progressLogForm.project_id = selectedProjectId || rfps[0].project_id;
    }
    
    if (!progressLogForm.project_id) {
      toast.error('Please select a project');
      return;
    }
    
    try {
      // Auto-generate title from date
      const now = new Date();
      const title = `Update - ${now.toLocaleDateString()}`;
      
      await contractorAxios.post(`${API}/contractor-portal/${contractor.id}/progress-logs`, {
        ...progressLogForm,
        title,
        project_id: progressLogForm.project_id || selectedProjectId,
        photo_metadata: progressLogForm.photo_metadata || [],
        created_at: now.toISOString()
      });
      
      toast.success('Progress log submitted!');
      setProgressLogForm({
        project_id: selectedProjectId,
        notes: '',
        photos: [],
        photo_metadata: [],
        attachments: [],
        gps_location: null
      });
      setShowProgressLogForm(false);
      fetchProgressLogs();
    } catch (error) {
      console.error('Error submitting progress log:', error);
      toast.error('Failed to submit progress log');
    }
  };
  
  const markMessageRead = async (messageId) => {
    try {
      await contractorAxios.put(`${API}/contractor-portal/${contractor.id}/messages/${messageId}/read`);
      fetchMessages();
    } catch (error) {
      console.error('Error marking message read:', error);
    }
  };

  const getStatusBadge = (rfp) => {
    if (rfp.awarded_to_me) {
      return <Badge className="bg-green-600 text-white"><Award className="w-3 h-3 mr-1" /> Awarded</Badge>;
    }
    if (rfp.bid_submitted) {
      return <Badge className="bg-blue-600 text-white"><CheckCircle2 className="w-3 h-3 mr-1" /> Bid Submitted</Badge>;
    }
    if (rfp.status === 'sent') {
      return <Badge className="bg-amber-600 text-white"><Timer className="w-3 h-3 mr-1" /> Awaiting Response</Badge>;
    }
    if (rfp.status === 'closed' || rfp.status === 'cancelled') {
      return <Badge variant="secondary"><XCircle className="w-3 h-3 mr-1" /> {rfp.status}</Badge>;
    }
    return <Badge variant="outline">{rfp.status}</Badge>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0D0D0D] to-[#1A1A1A] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-50 dark:bg-[#1A1A1A] border-slate-200 dark:border-[#2A2A2A]">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-[#70170F] rounded-full flex items-center justify-center mb-4">
              <Building2 className="w-8 h-8 text-slate-800 dark:text-[#E5E2DD]" />
            </div>
            <CardTitle className="text-2xl text-slate-800 dark:text-[#E5E2DD]">
              {isAdminView ? 'Manage Contractor Portal' : 'Contractor Portal'}
            </CardTitle>
            <CardDescription className="text-slate-500 dark:text-[#8A8A8A]">
              {isAdminView ? 'Select a contractor to view their portal' : 'Access your RFPs and submit bids'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isAdminView && allContractors.length > 0 ? (
              <>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4">
                  <div className="flex items-center gap-2 text-amber-400">
                    <Shield className="w-4 h-4" />
                    <span className="text-sm font-medium">Admin Mode</span>
                  </div>
                  <p className="text-xs text-amber-400/70 mt-1">Select a contractor to preview their portal view</p>
                </div>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {loadingContractors ? (
                    <div className="text-center py-4 text-slate-500 dark:text-[#8A8A8A]">Loading contractors...</div>
                  ) : (
                    allContractors.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => handleLogin(c.email)}
                        className="w-full p-3 rounded-lg border border-slate-200 dark:border-[#2A2A2A] hover:border-[#70170F] hover:bg-[#70170F]/10 transition-colors text-left"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#70170F]/20 flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-5 h-5 text-[#BC5D41]" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-800 dark:text-[#E5E2DD] truncate">{c.company_name}</p>
                            <p className="text-xs text-slate-400 dark:text-[#5A5A5A] truncate">{c.contact_name} • {c.email}</p>
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
                <Button 
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="w-full mt-4 border-slate-200 dark:border-[#2A2A2A] text-slate-500 dark:text-[#8A8A8A] hover:text-slate-800 dark:text-[#E5E2DD]"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
              </>
            ) : (
              <>
                <div>
                  <Label htmlFor="email" className="text-slate-800 dark:text-[#E5E2DD]">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                    className="mt-1 bg-white dark:bg-[#0D0D0D] border-slate-200 dark:border-[#2A2A2A] text-slate-800 dark:text-[#E5E2DD] placeholder:text-slate-400 dark:text-[#5A5A5A]"
                    data-testid="contractor-login-email"
                  />
                </div>
                <Button 
                  onClick={() => handleLogin()}
                  disabled={isLoggingIn}
                  className="w-full bg-[#70170F] hover:bg-[#8B1D12] text-slate-800 dark:text-[#E5E2DD]"
                  data-testid="contractor-login-btn"
                >
                  {isLoggingIn ? 'Signing in...' : 'Sign In'}
                </Button>
                <p className="text-xs text-center text-slate-400 dark:text-[#5A5A5A]">
                  Enter the email address registered with your contractor account
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main Portal Dashboard
  return (
    <div className="min-h-screen bg-white dark:bg-[#0D0D0D]">
      {/* Admin Mode Banner */}
      {isAdminView && (
        <div className="bg-amber-600 text-white px-3 py-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Shield className="w-4 h-4 flex-shrink-0" />
              <span className="text-xs font-medium truncate">Admin Preview — {contractor?.contact_name}</span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button 
                size="sm" 
                variant="outline" 
                className="h-7 bg-transparent border-white/50 text-white hover:bg-white/10 text-xs px-2"
                onClick={() => setShowAdminControls(true)}
              >
                <Settings className="w-3 h-3 mr-1" />
                Controls
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                className="h-7 bg-transparent border-white/50 text-white hover:bg-white/10 text-xs px-2"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="w-3 h-3 mr-1" />
                Back
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* PWA Install Prompt */}
      {showInstallPrompt && (
        <div className="bg-gradient-to-r from-[#70170F] to-[#8B1D12] text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Smartphone className="w-5 h-5" />
            </div>
            <div>
              <p className="font-medium text-sm">Add KEY-MADE to Home Screen</p>
              <p className="text-xs text-white/70">Quick access like a native app - no app store needed!</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              size="sm" 
              className="h-8 bg-white text-[#70170F] hover:bg-white/90"
              onClick={handleInstallPWA}
            >
              Install
            </Button>
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-8 text-white/70 hover:text-white hover:bg-white/10"
              onClick={dismissInstallPrompt}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
      
      {/* ===== APP-LIKE HEADER ===== */}
      <header className="bg-white dark:bg-[#111111] border-b border-slate-200 dark:border-[#2A2A2A] sticky top-0 z-20" data-testid="portal-header">
        <div className="max-w-7xl mx-auto px-4 py-3">
          {/* Top row: KEY-MADE brand + sign out */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#70170F] rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xs tracking-tight">KM</span>
              </div>
              <div className="min-w-0">
                <h1 className="text-sm font-bold text-slate-800 dark:text-[#E5E2DD] tracking-wide" data-testid="portal-header-tenant-name">
                  {tenantCompanyName || 'Contractor Portal'}
                </h1>
                <p className="text-xs text-[#BC5D41] font-medium">Site Office</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!isAdminView && (
                <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400 dark:text-[#5A5A5A] hover:text-slate-800 dark:text-[#E5E2DD] h-8 px-2">
                  Sign Out
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ===== MAIN CONTENT ===== */}
      <div className="max-w-7xl mx-auto">
        {activeTab ? (
          /* ===== SECTION VIEW (when a button is tapped) ===== */
          <div className="px-4 py-3">
            {/* Back to home button — large tap target for mobile */}
            <button
              onClick={() => setActiveTab(null)}
              className="flex items-center gap-2 text-[#BC5D41] hover:text-slate-800 dark:text-[#E5E2DD] transition-colors mb-4 py-2 px-3 -ml-3 rounded-lg hover:bg-slate-50 dark:bg-[#1A1A1A] active:scale-[0.97]"
              data-testid="back-to-home-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-base font-semibold">Back</span>
            </button>

            {/* Tabs container — hidden nav, content only */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">

          {/* Agreement Tab */}
          <TabsContent value="agreement">
            <ContractorAgreement 
              contractor={contractor} 
              isAdminView={isAdminView}
              onStatusChange={(newStatus) => {
                // Refresh contractor data when agreement status changes
                if (contractor?.email) handleLogin(contractor.email);
              }}
            />
          </TabsContent>

          {/* RFPs Tab */}
          <TabsContent value="rfps">
            <Card className="bg-slate-50 dark:bg-[#1A1A1A] border-slate-200 dark:border-[#2A2A2A]">
              <CardHeader>
                <CardTitle className="text-slate-800 dark:text-[#E5E2DD]">Your RFPs</CardTitle>
                <CardDescription className="text-slate-500 dark:text-[#8A8A8A]">
                  View and respond to requests for proposals
                </CardDescription>
              </CardHeader>
              <CardContent>
                {rfps.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 text-[#3A3A3A] mx-auto mb-4" />
                    <p className="text-slate-500 dark:text-[#8A8A8A]">No RFPs found</p>
                    <p className="text-sm text-slate-400 dark:text-[#5A5A5A]">You&apos;ll see RFPs here when you&apos;re invited to bid on projects</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {rfps.map((rfp, idx) => (
                      <div
                        key={`${rfp.id}-${idx}`}
                        onClick={() => viewRfpDetails(rfp)}
                        className="p-4 rounded-lg bg-white dark:bg-[#0D0D0D] border border-slate-200 dark:border-[#2A2A2A] hover:border-[#70170F] cursor-pointer transition-colors"
                        data-testid={`rfp-card-${rfp.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-medium text-slate-800 dark:text-[#E5E2DD] truncate">{rfp.title}</h3>
                              {getStatusBadge(rfp)}
                            </div>
                            <p className="text-sm text-slate-500 dark:text-[#8A8A8A] mb-2">{rfp.project_name}</p>
                            <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400 dark:text-[#5A5A5A]">
                              {rfp.project_address && (
                                <a 
                                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(rfp.project_address)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 hover:text-[#BC5D41] transition-colors"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <MapPin className="w-3 h-3" /> {rfp.project_address}
                                  <ExternalLink className="w-3 h-3 opacity-70" />
                                </a>
                              )}
                              {rfp.bid_due_date && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" /> Due: {formatDate(rfp.bid_due_date)}
                                </span>
                              )}
                            </div>
                          </div>
                      <ChevronRight className="w-5 h-5 text-slate-400 dark:text-[#5A5A5A] flex-shrink-0" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks">
            <Card className="bg-slate-50 dark:bg-[#1A1A1A] border-slate-200 dark:border-[#2A2A2A]">
              <CardHeader>
                <CardTitle className="text-slate-800 dark:text-[#E5E2DD]">Assigned Tasks</CardTitle>
                <CardDescription className="text-slate-500 dark:text-[#8A8A8A]">
                  Tasks and schedule items assigned to you. Confirm or decline your assignments.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {assignedTasks.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-12 h-12 text-[#3A3A3A] mx-auto mb-4" />
                    <p className="text-slate-500 dark:text-[#8A8A8A]">No tasks assigned</p>
                    <p className="text-sm text-slate-400 dark:text-[#5A5A5A]">When tasks are assigned to you, they&apos;ll appear here</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {assignedTasks.map(task => {
                      const daysUntilDue = getDaysUntilDue(task.due_date);
                      const isOverdue = daysUntilDue !== null && daysUntilDue < 0;
                      const isDueSoon = daysUntilDue !== null && daysUntilDue >= 0 && daysUntilDue <= 3;
                      
                      return (
                        <div
                          key={task.id}
                          className={`p-4 rounded-xl border transition-colors cursor-pointer ${
                            task.contractor_response === 'confirmed'
                              ? 'bg-green-500/10 border-green-500/30 hover:bg-green-500/20'
                              : task.contractor_response === 'declined'
                              ? 'bg-red-500/10 border-red-500/30 hover:bg-red-500/20'
                              : 'bg-white dark:bg-[#0D0D0D] border-slate-200 dark:border-[#2A2A2A] hover:bg-slate-50 dark:bg-[#1A1A1A]/50'
                          }`}
                          onClick={() => {
                            setSelectedTask(task);
                            setShowTaskDetailDialog(true);
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2 flex-wrap">
                                <h4 className="font-medium text-slate-800 dark:text-[#E5E2DD]">{task.title}</h4>
                                {/* Status Badge */}
                                {task.contractor_response === 'confirmed' && (
                                  <Badge className="bg-green-500/20 text-green-400 text-xs">
                                    ✓ Confirmed
                                  </Badge>
                                )}
                                {task.contractor_response === 'declined' && (
                                  <Badge className="bg-red-500/20 text-red-400 text-xs">
                                    ✗ Declined
                                  </Badge>
                                )}
                                {!task.contractor_response && (
                                  <Badge className="bg-blue-500/20 text-blue-400 text-xs">
                                    Needs Response
                                  </Badge>
                                )}
                                {/* Due Date */}
                                {daysUntilDue !== null && (
                                  <span className={`text-xs flex items-center gap-1 ${
                                    isOverdue ? 'text-red-400' : isDueSoon ? 'text-yellow-400' : 'text-slate-400 dark:text-[#5A5A5A]'
                                  }`}>
                                    <Clock className="w-3 h-3" />
                                    {isOverdue ? `${Math.abs(daysUntilDue)}d overdue` : `${daysUntilDue}d left`}
                                  </span>
                                )}
                              </div>
                              {task.description && (
                                <p className="text-sm text-slate-500 dark:text-[#8A8A8A] line-clamp-2">{task.description}</p>
                              )}
                              <div className="flex items-center gap-4 mt-3 text-xs text-slate-400 dark:text-[#5A5A5A]">
                                {task.project_name && (
                                  <span className="flex items-center gap-1">
                                    <Briefcase className="w-3 h-3" />
                                    {task.project_name}
                                  </span>
                                )}
                                {task.project_address && (
                                  <a 
                                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(task.project_address)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 hover:text-[#BC5D41] transition-colors"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <MapPin className="w-3 h-3" />
                                    {task.project_address}
                                    <ExternalLink className="w-3 h-3 opacity-70" />
                                  </a>
                                )}
                                {task.due_date && (
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    Due: {new Date(task.due_date).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-slate-400 dark:text-[#5A5A5A] flex-shrink-0" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Schedule/Gantt Tab */}
          <TabsContent value="schedule">
            <Card className="bg-slate-50 dark:bg-[#1A1A1A] border-slate-200 dark:border-[#2A2A2A]">
              <CardHeader>
                <CardTitle className="text-slate-800 dark:text-[#E5E2DD]">Project Schedule</CardTitle>
                <CardDescription className="text-slate-500 dark:text-[#8A8A8A]">
                  Visual timeline of your assigned tasks. Drag bars to adjust dates.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <GanttChart 
                  tasks={assignedTasks} 
                  onTaskUpdate={handleTaskDateUpdate}
                  onAddDependency={handleAddDependency}
                  onRemoveDependency={handleRemoveDependency}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents/Spec Sheets Tab */}
          <TabsContent value="documents">
            <Card className="bg-slate-50 dark:bg-[#1A1A1A] border-slate-200 dark:border-[#2A2A2A]">
              <CardHeader>
                <CardTitle className="text-slate-800 dark:text-[#E5E2DD]">Spec Sheets & Drawings</CardTitle>
                <CardDescription className="text-slate-500 dark:text-[#8A8A8A]">
                  Project specifications and drawings assigned to you
                </CardDescription>
              </CardHeader>
              <CardContent>
                {documents.length === 0 ? (
                  <div className="text-center py-12">
                    <Eye className="w-12 h-12 text-[#3A3A3A] mx-auto mb-4" />
                    <p className="text-slate-500 dark:text-[#8A8A8A]">No documents assigned yet</p>
                    <p className="text-sm text-slate-400 dark:text-[#5A5A5A]">Spec sheets and drawings will appear here when assigned</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="p-3 rounded-lg bg-white dark:bg-[#0D0D0D] border border-slate-200 dark:border-[#2A2A2A] hover:border-[#70170F] cursor-pointer transition-colors"
                      >
                        {doc.thumbnail_url ? (
                          <img 
                            src={doc.thumbnail_url} 
                            alt={doc.name}
                            className="w-full h-32 object-cover rounded mb-2"
                          />
                        ) : (
                          <div className="w-full h-32 bg-[#2A2A2A] rounded mb-2 flex items-center justify-center">
                            <FileText className="w-8 h-8 text-slate-400 dark:text-[#5A5A5A]" />
                          </div>
                        )}
                        <h4 className="text-sm font-medium text-slate-800 dark:text-[#E5E2DD] truncate">{doc.name}</h4>
                        {doc.spec_category && (
                          <Badge variant="outline" className="mt-1 text-xs">
                            {doc.spec_category}
                          </Badge>
                        )}
                        {doc.description && (
                          <p className="text-xs text-slate-400 dark:text-[#5A5A5A] mt-1 truncate">{doc.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card className="bg-slate-50 dark:bg-[#1A1A1A] border-slate-200 dark:border-[#2A2A2A]">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-slate-800 dark:text-[#E5E2DD]">Messages</CardTitle>
                  <CardDescription className="text-slate-500 dark:text-[#8A8A8A]">
                    Communication with the project team
                  </CardDescription>
                </div>
                <Button 
                  onClick={() => setShowMessageDialog(true)}
                  className="bg-[#70170F] hover:bg-[#8B1D12] text-slate-800 dark:text-[#E5E2DD]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Message
                </Button>
              </CardHeader>
              <CardContent>
                {messages.length === 0 ? (
                  <div className="text-center py-12">
                    <Mail className="w-12 h-12 text-[#3A3A3A] mx-auto mb-4" />
                    <p className="text-slate-500 dark:text-[#8A8A8A]">No messages yet</p>
                    <p className="text-sm text-slate-400 dark:text-[#5A5A5A]">Start a conversation with the project team</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        onClick={() => {
                          setSelectedMessage(msg);
                          if (!msg.is_read && msg.sender_type === 'admin') {
                            markMessageRead(msg.id);
                          }
                        }}
                        className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                          msg.sender_type === 'admin' && !msg.is_read
                            ? 'bg-[#70170F]/10 border-[#70170F]'
                            : 'bg-white dark:bg-[#0D0D0D] border-slate-200 dark:border-[#2A2A2A] hover:border-[#70170F]'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-slate-800 dark:text-[#E5E2DD]">
                                {msg.sender_type === 'admin' ? 'Project Team' : 'You'}
                              </span>
                              {msg.sender_type === 'admin' && !msg.is_read && (
                                <Badge className="bg-[#70170F] text-white text-xs">New</Badge>
                              )}
                            </div>
                            {msg.subject && <p className="text-sm text-slate-500 dark:text-[#8A8A8A]">{msg.subject}</p>}
                            <p className="text-sm text-slate-400 dark:text-[#5A5A5A] mt-1 line-clamp-2">{msg.message}</p>
                          </div>
                          <span className="text-xs text-slate-400 dark:text-[#5A5A5A]">{formatDate(msg.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Stage D — Plans Tab (transmitted issued sets) */}
          <TabsContent value="plans">
            {!openedPlansSet ? (
              <Card className="bg-slate-50 dark:bg-[#1A1A1A] border-slate-200 dark:border-[#2A2A2A]" data-testid="portal-plans-list">
                <CardHeader>
                  <CardTitle className="text-slate-800 dark:text-[#E5E2DD]">Plans</CardTitle>
                  <CardDescription className="text-slate-500 dark:text-[#8A8A8A]">
                    Issued drawing sets transmitted to you.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {plansSets.length === 0 ? (
                    <div className="text-center py-12" data-testid="portal-plans-empty">
                      <FileText className="w-12 h-12 text-[#3A3A3A] mx-auto mb-4" />
                      <p className="text-slate-500 dark:text-[#8A8A8A]">No plans have been shared with you yet.</p>
                      <p className="text-sm text-slate-400 dark:text-[#5A5A5A]">When a designer transmits a set to you, it will appear here.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {plansSets.map((s) => (
                        <div
                          key={s.transmittal_id}
                          onClick={async () => {
                            setLoadingPlansSet(true);
                            try {
                              const r = await contractorAxios.get(`${API}/contractor-portal/plans/sets/${s.transmittal_id}`);
                              setOpenedPlansSet(r.data);
                              // Bump the plans badge down by 1 optimistically (first_viewed_at just stamped).
                              if (!s.first_viewed_at) {
                                setPortalBadges((b) => ({ ...b, plans: Math.max(0, (b.plans || 0) - 1) }));
                              }
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || 'Could not open set');
                            } finally {
                              setLoadingPlansSet(false);
                            }
                          }}
                          className="p-4 rounded-lg bg-white dark:bg-[#0D0D0D] border border-slate-200 dark:border-[#2A2A2A] hover:border-[#70170F] cursor-pointer transition-colors"
                          data-testid={`plans-set-card-${s.transmittal_id}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold text-slate-800 dark:text-[#E5E2DD] truncate">{s.set_name}</span>
                                {!s.first_viewed_at && (
                                  <Badge className="bg-[#70170F] text-white text-[10px]" data-testid={`plans-set-new-${s.transmittal_id}`}>NEW</Badge>
                                )}
                              </div>
                              <div className="text-xs text-slate-500 dark:text-[#8A8A8A] space-y-0.5">
                                <div>{s.project_name}</div>
                                <div>{s.phase} · {s.issue_date} · {s.sheet_count || 0} sheet{(s.sheet_count || 0) === 1 ? '' : 's'}</div>
                                <div>Transmitted by {s.sent_by_name || '—'}</div>
                                {s.note && <div className="italic mt-1">“{s.note}”</div>}
                              </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-slate-400 dark:text-[#5A5A5A] mt-1" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-slate-50 dark:bg-[#1A1A1A] border-slate-200 dark:border-[#2A2A2A]" data-testid="portal-plans-set-open">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-slate-800 dark:text-[#E5E2DD]" data-testid="plans-set-title">{openedPlansSet.transmittal?.set_name}</CardTitle>
                      <CardDescription className="text-slate-500 dark:text-[#8A8A8A]">
                        {openedPlansSet.transmittal?.phase} · {openedPlansSet.transmittal?.issue_date} · {openedPlansSet.sheet_count} sheet{openedPlansSet.sheet_count === 1 ? '' : 's'}
                      </CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setOpenedPlansSet(null)} data-testid="plans-back-btn">
                      <ArrowLeft className="w-4 h-4 mr-1" /> Back
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingPlansSet ? (
                    <div className="py-8 text-center text-slate-500 dark:text-[#8A8A8A]"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
                  ) : (openedPlansSet.sheets || []).length === 0 ? (
                    <div className="py-8 text-center text-slate-500 dark:text-[#8A8A8A]" data-testid="plans-set-no-sheets">
                      No sheets have been marked visible for you in this set.
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="plans-sheet-grid">
                      {(openedPlansSet.sheets || []).map((sh) => (
                        <div key={`${sh.drawing_id}-${sh.version_id}`}
                             className="rounded-lg border border-slate-200 dark:border-[#2A2A2A] bg-white dark:bg-[#0D0D0D] overflow-hidden"
                             data-testid={`plans-sheet-${sh.drawing_id}`}>
                          <div className="aspect-[4/3] bg-slate-100 dark:bg-[#1A1A1A] flex items-center justify-center relative">
                            <DrawingCard
isSuperAdmin={false}
                              drawing={{
                                id: sh.drawing_id,
                                thumbnail_url: sh.thumbnail_url,
                                current_file_url: sh.file_url || sh.current_file_url,
                                current_version_id: sh.version_id,
                              }}
                              testId={`plans-sheet-thumb-${sh.drawing_id}`}
                              className="w-full h-full object-cover"
                              fallbackIconClassName="w-10 h-10 text-slate-400 dark:text-[#5A5A5A]"
                            />
                          </div>
                          <div className="p-3">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono text-xs text-[#BC5D41]">{sh.sheet_number || sh.drawing_number}</span>
                              <Badge className="bg-slate-200 dark:bg-[#2A2A2A] text-[10px] text-slate-700 dark:text-[#E5E2DD]">rev {sh.revision_code || '—'}</Badge>
                            </div>
                            <p className="text-sm truncate text-slate-800 dark:text-[#E5E2DD]">{sh.sheet_title}</p>
                            <div className="flex gap-1.5 mt-2">
                              <Button
                                variant="outline" size="sm" className="h-7 text-xs flex-1"
                                disabled={!sh.has_file}
                                onClick={async () => {
                                  setPlansSheetBlob({ id: sh.drawing_id, url: null, loading: true });
                                  try {
                                    const tok = sessionStorage.getItem(CONTRACTOR_SESSION_KEY);
                                    const r = await fetch(`${API}/contractor-portal/plans/file/${sh.drawing_id}/${sh.version_id}`, {
                                      headers: { 'X-Contractor-Session': tok || '' },
                                    });
                                    if (!r.ok) throw new Error(`${r.status}`);
                                    const blob = await r.blob();
                                    const url = URL.createObjectURL(blob);
                                    setPlansSheetBlob({ id: sh.drawing_id, url, loading: false });
                                    window.open(url, '_blank');
                                  } catch (e) {
                                    toast.error('Could not open sheet');
                                    setPlansSheetBlob({ id: null, url: null, loading: false });
                                  }
                                }}
                                data-testid={`plans-sheet-view-${sh.drawing_id}`}
                              >
                                <Eye className="w-3 h-3 mr-1" /> View
                              </Button>
                              <Button
                                variant="outline" size="sm" className="h-7 text-xs flex-1"
                                disabled={!sh.has_file}
                                onClick={async () => {
                                  try {
                                    const tok = sessionStorage.getItem(CONTRACTOR_SESSION_KEY);
                                    const r = await fetch(`${API}/contractor-portal/plans/file/${sh.drawing_id}/${sh.version_id}`, {
                                      headers: { 'X-Contractor-Session': tok || '' },
                                    });
                                    if (!r.ok) throw new Error(`${r.status}`);
                                    const blob = await r.blob();
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `${sh.sheet_number || sh.drawing_id}.pdf`;
                                    document.body.appendChild(a); a.click(); a.remove();
                                    setTimeout(() => URL.revokeObjectURL(url), 1000);
                                  } catch (e) {
                                    toast.error('Download failed');
                                  }
                                }}
                                data-testid={`plans-sheet-download-${sh.drawing_id}`}
                              >
                                <Download className="w-3 h-3 mr-1" /> Save
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Punch List Tab */}
          <TabsContent value="punch-list">            <Card className="bg-white border-slate-200 dark:bg-slate-50 dark:bg-[#1A1A1A] dark:border-slate-200 dark:border-[#2A2A2A]">
              <CardHeader>
                <CardTitle className="text-slate-800 dark:text-[#E5E2DD]">Punch List</CardTitle>
                <CardDescription className="text-slate-500 dark:text-[#8A8A8A]">
                  Deficiency items assigned to you. Acknowledge, respond, and upload photos.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ContractorPunchList 
                  contractorId={contractor?.id}
                  contractorEmail={contractor?.email}
                />
              </CardContent>
            </Card>
          </TabsContent>


          {/* Quality Checklists Tab */}
          <TabsContent value="quality">
            <ContractorQualityChecklist contractor={contractor} projectId={rfps[0]?.project_id} />
          </TabsContent>

          {/* Progress Log Tab */}
          <TabsContent value="progress">
            <Card className="bg-slate-50 dark:bg-[#1A1A1A] border-slate-200 dark:border-[#2A2A2A]">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-slate-800 dark:text-[#E5E2DD]">Progress Log</CardTitle>
                  <CardDescription className="text-slate-500 dark:text-[#8A8A8A]">
                    Share jobsite updates and daily progress
                  </CardDescription>
                </div>
                <Button 
                  onClick={() => setShowProgressLogForm(true)}
                  className="bg-[#70170F] hover:bg-[#8B1D12] text-slate-800 dark:text-[#E5E2DD]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Entry
                </Button>
              </CardHeader>
              <CardContent>
                {progressLogs.length === 0 ? (
                  <div className="text-center py-12">
                    <Clock className="w-12 h-12 text-[#3A3A3A] mx-auto mb-4" />
                    <p className="text-slate-500 dark:text-[#8A8A8A]">No progress logs yet</p>
                    <p className="text-sm text-slate-400 dark:text-[#5A5A5A]">Share jobsite updates to keep the team informed</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {progressLogs.map((log) => (
                      <div
                        key={log.id}
                        className="p-4 rounded-lg bg-white dark:bg-[#0D0D0D] border border-slate-200 dark:border-[#2A2A2A]"
                      >
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <div>
                            <h4 className="font-medium text-slate-800 dark:text-[#E5E2DD]">{log.title}</h4>
                            <p className="text-xs text-slate-400 dark:text-[#5A5A5A]">{formatDate(log.created_at)}</p>
                          </div>
                          <Badge 
                            variant={log.status === 'approved' ? 'default' : 'outline'}
                            className={log.status === 'approved' ? 'bg-green-600' : ''}
                          >
                            {log.status === 'approved' ? 'Approved' : 'Pending Review'}
                          </Badge>
                        </div>
                        {log.work_performed && (
                          <div className="mb-2">
                            <p className="text-xs text-slate-400 dark:text-[#5A5A5A]">Work Performed</p>
                            <p className="text-sm text-slate-500 dark:text-[#8A8A8A]">{log.work_performed}</p>
                          </div>
                        )}
                        {log.notes && (
                          <p className="text-sm text-slate-500 dark:text-[#8A8A8A]">{log.notes}</p>
                        )}
                        <div className="flex flex-wrap gap-4 mt-3 text-xs text-slate-400 dark:text-[#5A5A5A]">
                          {log.workers_on_site && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" /> {log.workers_on_site} workers
                            </span>
                          )}
                          {log.hours_worked && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {log.hours_worked} hours
                            </span>
                          )}
                          {log.approved_for_client && (
                            <Badge className="bg-blue-600 text-white text-xs">
                              <Eye className="w-3 h-3 mr-1" /> Shared with Client
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
            </Tabs>
          </div>
        ) : (
          /* ===== HOME SCREEN (large button grid) ===== */
          <div className="px-4 py-4" data-testid="portal-home">
            {/* Contractor Identity */}
            <div className="mb-4">
              <p className="text-xl font-bold text-slate-800 dark:text-[#E5E2DD]" data-testid="contractor-name">
                {(() => {
                  const hour = new Date().getHours();
                  const firstName = contractor?.contact_name?.split(' ')[0] || '';
                  if (hour < 12) return `Good morning${firstName ? ', ' + firstName : ''}`;
                  if (hour < 17) return `Good afternoon${firstName ? ', ' + firstName : ''}`;
                  return `Good evening${firstName ? ', ' + firstName : ''}`;
                })()}
              </p>
              <p className="text-sm text-slate-500 dark:text-[#8A8A8A]">{contractor?.company_name}</p>
            </div>

            {/* ===== PROJECT HEADER BLOCKS =====
                Wave 5.9 build 4 — one header per unique membership project.
                Dedupe by project_id defensively (backend already
                deduplicates via list(set(...)), but a legacy duplicate
                row would still produce two cards; guard here). */}
            {(() => {
              const seen = new Set();
              const unique = (portalProjects || []).filter((p) => {
                const key = p?.project_id;
                if (!key || seen.has(key)) return false;
                seen.add(key);
                return true;
              });
              if (unique.length > 0) {
                return unique.map((p, idx) => (
                  <ProjectHeader key={p.project_id} project={p} index={idx} />
                ));
              }
              if (rfps.length > 0) {
                return (
                  <ProjectHeader
                    index={0}
                    project={{
                      project_id: rfps[0]?.project_id,
                      name: rfps[0]?.project_name,
                      image: rfps[0]?.project_image || rfps[0]?.project_image_thumbnail,
                      site_address: rfps[0]?.project_address,
                    }}
                  />
                );
              }
              return null;
            })()}

            {/* Quick Stats Row */}
            <div className="flex gap-2 flex-wrap mb-5">
              {summary.awaiting_response > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-full px-3 py-1.5 flex items-center gap-2 text-xs">
                  <Timer className="w-3 h-3 text-amber-400" />
                  <span className="text-amber-400 font-medium">{summary.awaiting_response} Pending</span>
                </div>
              )}
              {assignedTasks.filter(t => !t.contractor_response).length > 0 && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-full px-3 py-1.5 flex items-center gap-2 text-xs">
                  <CheckCircle className="w-3 h-3 text-blue-400" />
                  <span className="text-blue-400 font-medium">{assignedTasks.filter(t => !t.contractor_response).length} Tasks</span>
                </div>
              )}
              {unreadMessages > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-full px-3 py-1.5 flex items-center gap-2 text-xs">
                  <Mail className="w-3 h-3 text-red-400" />
                  <span className="text-red-400 font-medium">{unreadMessages} Unread</span>
                </div>
              )}
            </div>

            {/* ===== LARGE NAVIGATION BUTTONS ===== */}
            <div className="grid grid-cols-2 gap-3" data-testid="portal-nav-buttons">
              {/* 1. Plans — Stage D: transmitted sets, badge counts unseen */}
              <button
                onClick={() => {
                  setActiveTab('plans');
                  // Refresh + auto-open the single set if there's only one.
                  contractorAxios.get(`${API}/contractor-portal/plans/sets`)
                    .then((r) => setPlansSets(r.data?.sets || []))
                    .catch(() => {});
                }}
                className="relative flex flex-col items-center justify-center gap-2 p-5 rounded-xl bg-slate-50 dark:bg-[#1A1A1A] border border-slate-200 dark:border-[#2A2A2A] hover:border-[#70170F] hover:bg-[#70170F]/10 transition-all active:scale-[0.97]"
                data-testid="nav-btn-plans"
              >
                <FileText className="w-7 h-7 text-[#BC5D41]" />
                <span className="text-sm font-semibold text-slate-800 dark:text-[#E5E2DD]">Plans</span>
                {portalBadges.plans > 0 && (
                  <span className="absolute top-2 right-2 min-w-[20px] h-5 px-1 bg-[#70170F] text-white text-[10px] rounded-full flex items-center justify-center font-bold" data-testid="nav-btn-plans-badge">{portalBadges.plans}</span>
                )}
              </button>

              {/* 2. Spec Sheets */}
              <button
                onClick={() => setActiveTab('documents')}
                className="flex flex-col items-center justify-center gap-2 p-5 rounded-xl bg-slate-50 dark:bg-[#1A1A1A] border border-slate-200 dark:border-[#2A2A2A] hover:border-[#70170F] hover:bg-[#70170F]/10 transition-all active:scale-[0.97]"
                data-testid="nav-btn-specs"
              >
                <Eye className="w-7 h-7 text-[#BC5D41]" />
                <span className="text-sm font-semibold text-slate-800 dark:text-[#E5E2DD]">Spec Sheets</span>
              </button>

              {/* 3. Progress Log — v7.2 (2026-07-12) restyled to
                  match sibling tiles (Plans, Spec Sheets, RFPs, etc.). */}
              <button
                onClick={() => setShowProgressLogForm(true)}
                className="relative flex flex-col items-center justify-center gap-2 p-5 rounded-xl bg-slate-50 dark:bg-[#1A1A1A] border border-slate-200 dark:border-[#2A2A2A] hover:border-[#70170F] hover:bg-[#70170F]/10 transition-all active:scale-[0.97]"
                data-testid="nav-btn-progress"
              >
                <Camera className="w-7 h-7 text-[#BC5D41]" />
                <span className="text-sm font-semibold text-slate-800 dark:text-[#E5E2DD]">Progress Log</span>
              </button>

              {/* 4. RFPs */}
              <button
                onClick={() => setActiveTab('rfps')}
                className="relative flex flex-col items-center justify-center gap-2 p-5 rounded-xl bg-slate-50 dark:bg-[#1A1A1A] border border-slate-200 dark:border-[#2A2A2A] hover:border-[#70170F] hover:bg-[#70170F]/10 transition-all active:scale-[0.97]"
                data-testid="nav-btn-rfps"
              >
                <Briefcase className="w-7 h-7 text-[#BC5D41]" />
                <span className="text-sm font-semibold text-slate-800 dark:text-[#E5E2DD]">RFPs</span>
                {portalBadges.rfps > 0 && (
                  <span className="absolute top-2 right-2 min-w-[20px] h-5 px-1 bg-amber-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold" data-testid="nav-btn-rfps-badge">{portalBadges.rfps}</span>
                )}
              </button>

              {/* 5. Tasks */}
              <button
                onClick={() => setActiveTab('tasks')}
                className="relative flex flex-col items-center justify-center gap-2 p-5 rounded-xl bg-slate-50 dark:bg-[#1A1A1A] border border-slate-200 dark:border-[#2A2A2A] hover:border-[#70170F] hover:bg-[#70170F]/10 transition-all active:scale-[0.97]"
                data-testid="nav-btn-tasks"
              >
                <CheckCircle className="w-7 h-7 text-[#BC5D41]" />
                <span className="text-sm font-semibold text-slate-800 dark:text-[#E5E2DD]">Tasks</span>
                {assignedTasks.filter(t => !t.contractor_response).length > 0 && (
                  <span className="absolute top-2 right-2 w-5 h-5 bg-blue-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">{assignedTasks.filter(t => !t.contractor_response).length}</span>
                )}
              </button>

              {/* 6. Schedule */}
              <button
                onClick={() => setActiveTab('schedule')}
                className="flex flex-col items-center justify-center gap-2 p-5 rounded-xl bg-slate-50 dark:bg-[#1A1A1A] border border-slate-200 dark:border-[#2A2A2A] hover:border-[#70170F] hover:bg-[#70170F]/10 transition-all active:scale-[0.97]"
                data-testid="nav-btn-schedule"
              >
                <Calendar className="w-7 h-7 text-[#BC5D41]" />
                <span className="text-sm font-semibold text-slate-800 dark:text-[#E5E2DD]">Schedule</span>
              </button>

              {/* 7. Messages */}
              <button
                onClick={() => setActiveTab('messages')}
                className="relative flex flex-col items-center justify-center gap-2 p-5 rounded-xl bg-slate-50 dark:bg-[#1A1A1A] border border-slate-200 dark:border-[#2A2A2A] hover:border-[#70170F] hover:bg-[#70170F]/10 transition-all active:scale-[0.97]"
                data-testid="nav-btn-messages"
              >
                <Mail className="w-7 h-7 text-[#BC5D41]" />
                <span className="text-sm font-semibold text-slate-800 dark:text-[#E5E2DD]">Messages</span>
                {unreadMessages > 0 && (
                  <span className="absolute top-2 right-2 w-5 h-5 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">{unreadMessages}</span>
                )}
              </button>

              {/* 8. Punch List */}
              <button
                onClick={() => setActiveTab('punch-list')}
                className="relative flex flex-col items-center justify-center gap-2 p-5 rounded-xl bg-slate-50 dark:bg-[#1A1A1A] border border-slate-200 dark:border-[#2A2A2A] hover:border-[#70170F] hover:bg-[#70170F]/10 transition-all active:scale-[0.97]"
                data-testid="nav-btn-punchlist"
              >
                <ClipboardList className="w-7 h-7 text-[#BC5D41]" />
                <span className="text-sm font-semibold text-slate-800 dark:text-[#E5E2DD]">Punch List</span>
                {portalBadges.punch_list > 0 && (
                  <span className="absolute top-2 right-2 min-w-[20px] h-5 px-1 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold" data-testid="nav-btn-punchlist-badge">{portalBadges.punch_list}</span>
                )}
              </button>

              {/* 9. Quality Checklist */}
              <button
                onClick={() => setActiveTab('quality')}
                className="flex flex-col items-center justify-center gap-2 p-5 rounded-xl bg-slate-50 dark:bg-[#1A1A1A] border border-slate-200 dark:border-[#2A2A2A] hover:border-[#70170F] hover:bg-[#70170F]/10 transition-all active:scale-[0.97]"
                data-testid="nav-btn-quality"
              >
                <CheckCircle className="w-7 h-7 text-[#BC5D41]" />
                <span className="text-sm font-semibold text-slate-800 dark:text-[#E5E2DD]">Quality Check</span>
              </button>

              {/* 10. Agreement — LAST */}
              <button
                onClick={() => setActiveTab('agreement')}
                className="col-span-2 flex items-center justify-center gap-3 p-4 rounded-xl bg-slate-50 dark:bg-[#1A1A1A] border border-slate-200 dark:border-[#2A2A2A] hover:border-[#70170F] hover:bg-[#70170F]/10 transition-all active:scale-[0.97]"
                data-testid="nav-btn-agreement"
              >
                <FileSignature className="w-6 h-6 text-[#BC5D41]" />
                <span className="text-sm font-semibold text-slate-800 dark:text-[#E5E2DD]">Contractor Agreement</span>
              </button>
            </div>

            {/* Date footer */}
            <div className="flex items-center justify-center gap-1 mt-5 text-xs text-slate-400 dark:text-[#5A5A5A]">
              <Clock className="w-3 h-3" />
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
          </div>
        )}
      </div>

      {/* Wave 5.9 — the old "Engineered by KEY-MADE" tagline has been
          removed; GlobalFooter (rendered near the end of this component)
          is the single source for the tenant + KEY-MADE footer mark. */}

      {/* Task Detail Dialog */}
      <Dialog open={showTaskDetailDialog} onOpenChange={setShowTaskDetailDialog}>
        <DialogContent className="max-w-lg bg-slate-50 dark:bg-[#1A1A1A] border-slate-200 dark:border-[#2A2A2A]">
          <DialogHeader>
            <DialogTitle className="text-slate-800 dark:text-[#E5E2DD]">{selectedTask?.title}</DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-[#8A8A8A]">
              {selectedTask?.project_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Task Description */}
            {selectedTask?.description && (
              <div>
                <Label className="text-xs text-slate-400 dark:text-[#5A5A5A]">Description</Label>
                <p className="text-sm text-slate-800 dark:text-[#E5E2DD] mt-1">{selectedTask.description}</p>
              </div>
            )}
            
            {/* Task Details */}
            <div className="grid grid-cols-2 gap-4">
              {selectedTask?.due_date && (
                <div>
                  <Label className="text-xs text-slate-400 dark:text-[#5A5A5A]">Due Date</Label>
                  <p className="text-sm text-slate-800 dark:text-[#E5E2DD] mt-1 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {new Date(selectedTask.due_date).toLocaleDateString()}
                  </p>
                </div>
              )}
              {selectedTask?.project_address && (
                <div>
                  <Label className="text-xs text-slate-400 dark:text-[#5A5A5A]">Location</Label>
                  <a 
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedTask.project_address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[#BC5D41] mt-1 flex items-center gap-2 hover:underline"
                  >
                    <MapPin className="w-4 h-4" />
                    View on Map
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
            </div>
            
            {/* Response Status */}
            {selectedTask?.contractor_response && (
              <div className={`p-3 rounded-lg ${
                selectedTask.contractor_response === 'confirmed'
                  ? 'bg-green-500/10 border border-green-500/30'
                  : 'bg-red-500/10 border border-red-500/30'
              }`}>
                <p className={`text-sm font-medium ${
                  selectedTask.contractor_response === 'confirmed' ? 'text-green-400' : 'text-red-400'
                }`}>
                  {selectedTask.contractor_response === 'confirmed' ? '✓ You confirmed this task' : '✗ You declined this task'}
                </p>
                {selectedTask.contractor_response_notes && (
                  <p className="text-xs text-slate-500 dark:text-[#8A8A8A] mt-1">{selectedTask.contractor_response_notes}</p>
                )}
                {selectedTask.contractor_response_at && (
                  <p className="text-xs text-slate-400 dark:text-[#5A5A5A] mt-1">
                    Responded: {new Date(selectedTask.contractor_response_at).toLocaleString()}
                  </p>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter className="mt-4">
            {!selectedTask?.contractor_response ? (
              <>
                <Button
                  variant="outline"
                  className="border-red-500 text-red-400 hover:bg-red-500/10"
                  onClick={() => handleTaskResponse(selectedTask?.id, 'declined')}
                >
                  <X className="w-4 h-4 mr-2" />
                  Decline
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => handleTaskResponse(selectedTask?.id, 'confirmed')}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirm
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                className="border-slate-200 dark:border-[#2A2A2A] text-slate-500 dark:text-[#8A8A8A]"
                onClick={() => setShowTaskDetailDialog(false)}
              >
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RFP Detail Dialog */}
      <Dialog open={showRfpDetail} onOpenChange={setShowRfpDetail}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col bg-slate-50 dark:bg-[#1A1A1A] border-slate-200 dark:border-[#2A2A2A]">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-slate-800 dark:text-[#E5E2DD]">{selectedRfp?.title}</DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-[#8A8A8A]">
              {selectedRfp?.project_name} • {selectedRfp?.rfp_number}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto min-h-0 space-y-6 py-4">
            {/* Project Info */}
            <div className="p-4 rounded-lg bg-white dark:bg-[#0D0D0D] border border-slate-200 dark:border-[#2A2A2A]">
              <h4 className="font-medium text-slate-800 dark:text-[#E5E2DD] mb-3 flex items-center gap-2">
                <Briefcase className="w-4 h-4" /> Project Information
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-400 dark:text-[#5A5A5A]">Project Name</p>
                  <p className="text-slate-800 dark:text-[#E5E2DD]">{rfpDetails?.project?.name || selectedRfp?.project_name}</p>
                </div>
                <div>
                  <p className="text-slate-400 dark:text-[#5A5A5A]">Address</p>
                  {selectedRfp?.project_address ? (
                    <a 
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedRfp.project_address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-800 dark:text-[#E5E2DD] hover:text-[#BC5D41] transition-colors flex items-center gap-1"
                    >
                      {selectedRfp.project_address}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <p className="text-slate-800 dark:text-[#E5E2DD]">Not specified</p>
                  )}
                </div>
                <div>
                  <p className="text-slate-400 dark:text-[#5A5A5A]">Bid Due Date</p>
                  <p className="text-slate-800 dark:text-[#E5E2DD]">{formatDate(selectedRfp?.bid_due_date)}</p>
                </div>
                <div>
                  <p className="text-slate-400 dark:text-[#5A5A5A]">Project Start</p>
                  <p className="text-slate-800 dark:text-[#E5E2DD]">{formatDate(selectedRfp?.project_start_date)}</p>
                </div>
              </div>
            </div>

            {/* Scope of Work */}
            {rfpDetails?.rfp?.scope_summary && (
              <div className="p-4 rounded-lg bg-white dark:bg-[#0D0D0D] border border-slate-200 dark:border-[#2A2A2A]">
                <h4 className="font-medium text-slate-800 dark:text-[#E5E2DD] mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Scope of Work
                </h4>
                <p className="text-sm text-slate-500 dark:text-[#8A8A8A] whitespace-pre-wrap">{rfpDetails.rfp.scope_summary}</p>
              </div>
            )}

            {/* Scope Items */}
            {rfpDetails?.rfp?.scope_items?.length > 0 && (
              <div className="p-4 rounded-lg bg-white dark:bg-[#0D0D0D] border border-slate-200 dark:border-[#2A2A2A]">
                <h4 className="font-medium text-slate-800 dark:text-[#E5E2DD] mb-3">Scope Items</h4>
                <div className="space-y-2">
                  {rfpDetails.rfp.scope_items.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-slate-800 dark:text-[#E5E2DD]">{item.description}</p>
                        {item.quantity && <p className="text-slate-400 dark:text-[#5A5A5A]">Qty: {item.quantity} {item.unit}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Attachments */}
            {rfpDetails?.rfp?.attachments?.length > 0 && (
              <div className="p-4 rounded-lg bg-white dark:bg-[#0D0D0D] border border-slate-200 dark:border-[#2A2A2A]">
                <h4 className="font-medium text-slate-800 dark:text-[#E5E2DD] mb-3 flex items-center gap-2">
                  <Download className="w-4 h-4" /> Attachments
                </h4>
                <div className="space-y-2">
                  {rfpDetails.rfp.attachments.map((att, idx) => (
                    <a 
                      key={idx}
                      href={att.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300"
                    >
                      <FileText className="w-4 h-4" />
                      {att.name}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Existing Bid */}
            {rfpDetails?.existing_bid && (
              <div className="p-4 rounded-lg bg-green-900/20 border border-green-800">
                <h4 className="font-medium text-green-400 mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> Your Submitted Bid
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-400 dark:text-[#5A5A5A]">Total Amount</p>
                    <p className="text-xl font-bold text-slate-800 dark:text-[#E5E2DD]">{formatCurrency(rfpDetails.existing_bid.total_amount)}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 dark:text-[#5A5A5A]">Submitted</p>
                    <p className="text-slate-800 dark:text-[#E5E2DD]">{formatDate(rfpDetails.existing_bid.submitted_at)}</p>
                  </div>
                </div>
                {rfpDetails.existing_bid.notes && (
                  <div className="mt-3">
                    <p className="text-slate-400 dark:text-[#5A5A5A] text-sm">Notes</p>
                    <p className="text-slate-500 dark:text-[#8A8A8A] text-sm">{rfpDetails.existing_bid.notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="flex-shrink-0 pt-4 border-t border-slate-200 dark:border-[#2A2A2A]">
            <Button variant="outline" onClick={() => setShowRfpDetail(false)} className="border-slate-200 dark:border-[#2A2A2A] text-slate-800 dark:text-[#E5E2DD]">
              Close
            </Button>
            {selectedRfp?.status !== 'awarded' && selectedRfp?.status !== 'closed' && selectedRfp?.status !== 'cancelled' && (
              <Button 
                onClick={() => setShowBidForm(true)}
                className="bg-[#70170F] hover:bg-[#8B1D12] text-slate-800 dark:text-[#E5E2DD]"
                data-testid="submit-bid-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {rfpDetails?.existing_bid ? 'Update Bid' : 'Submit Bid'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bid Submission Dialog */}
      <Dialog open={showBidForm} onOpenChange={setShowBidForm}>
        <DialogContent className="max-w-lg bg-slate-50 dark:bg-[#1A1A1A] border-slate-200 dark:border-[#2A2A2A]">
          <DialogHeader>
            <DialogTitle className="text-slate-800 dark:text-[#E5E2DD]">Submit Your Bid</DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-[#8A8A8A]">
              {selectedRfp?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="total_amount" className="text-slate-800 dark:text-[#E5E2DD]">Total Bid Amount *</Label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-[#5A5A5A]" />
                <Input
                  id="total_amount"
                  type="number"
                  placeholder="0.00"
                  value={bidForm.total_amount}
                  onChange={(e) => setBidForm({ ...bidForm, total_amount: e.target.value })}
                  className="pl-9 bg-white dark:bg-[#0D0D0D] border-slate-200 dark:border-[#2A2A2A] text-slate-800 dark:text-[#E5E2DD]"
                  data-testid="bid-total-amount"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="labor_amount" className="text-slate-800 dark:text-[#E5E2DD]">Labor</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-[#5A5A5A]" />
                  <Input
                    id="labor_amount"
                    type="number"
                    placeholder="0.00"
                    value={bidForm.labor_amount}
                    onChange={(e) => setBidForm({ ...bidForm, labor_amount: e.target.value })}
                    className="pl-9 bg-white dark:bg-[#0D0D0D] border-slate-200 dark:border-[#2A2A2A] text-slate-800 dark:text-[#E5E2DD]"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="materials_amount" className="text-slate-800 dark:text-[#E5E2DD]">Materials</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-[#5A5A5A]" />
                  <Input
                    id="materials_amount"
                    type="number"
                    placeholder="0.00"
                    value={bidForm.materials_amount}
                    onChange={(e) => setBidForm({ ...bidForm, materials_amount: e.target.value })}
                    className="pl-9 bg-white dark:bg-[#0D0D0D] border-slate-200 dark:border-[#2A2A2A] text-slate-800 dark:text-[#E5E2DD]"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="estimated_duration" className="text-slate-800 dark:text-[#E5E2DD]">Est. Duration</Label>
                <Input
                  id="estimated_duration"
                  placeholder="e.g., 2 weeks"
                  value={bidForm.estimated_duration}
                  onChange={(e) => setBidForm({ ...bidForm, estimated_duration: e.target.value })}
                  className="mt-1 bg-white dark:bg-[#0D0D0D] border-slate-200 dark:border-[#2A2A2A] text-slate-800 dark:text-[#E5E2DD]"
                />
              </div>
              <div>
                <Label htmlFor="availability_date" className="text-slate-800 dark:text-[#E5E2DD]">Available From</Label>
                <Input
                  id="availability_date"
                  type="date"
                  value={bidForm.availability_date}
                  onChange={(e) => setBidForm({ ...bidForm, availability_date: e.target.value })}
                  className="mt-1 bg-white dark:bg-[#0D0D0D] border-slate-200 dark:border-[#2A2A2A] text-slate-800 dark:text-[#E5E2DD]"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="notes" className="text-slate-800 dark:text-[#E5E2DD]">Notes / Comments</Label>
              <Textarea
                id="notes"
                placeholder="Any additional information about your bid..."
                value={bidForm.notes}
                onChange={(e) => setBidForm({ ...bidForm, notes: e.target.value })}
                className="mt-1 bg-white dark:bg-[#0D0D0D] border-slate-200 dark:border-[#2A2A2A] text-slate-800 dark:text-[#E5E2DD] min-h-[100px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBidForm(false)} className="border-slate-200 dark:border-[#2A2A2A] text-slate-800 dark:text-[#E5E2DD]">
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitBid}
              disabled={isSubmittingBid}
              className="bg-[#70170F] hover:bg-[#8B1D12] text-slate-800 dark:text-[#E5E2DD]"
              data-testid="confirm-submit-bid"
            >
              {isSubmittingBid ? 'Submitting...' : 'Submit Bid'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Message Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent className="max-w-lg bg-slate-50 dark:bg-[#1A1A1A] border-slate-200 dark:border-[#2A2A2A]">
          <DialogHeader>
            <DialogTitle className="text-slate-800 dark:text-[#E5E2DD]">Send Message</DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-[#8A8A8A]">
              Send a message to the project team
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="msg-subject" className="text-slate-800 dark:text-[#E5E2DD]">Subject (Optional)</Label>
              <Input
                id="msg-subject"
                placeholder="Message subject"
                value={newMessage.subject}
                onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })}
                className="mt-1 bg-white dark:bg-[#0D0D0D] border-slate-200 dark:border-[#2A2A2A] text-slate-800 dark:text-[#E5E2DD]"
              />
            </div>
            <div>
              <Label htmlFor="msg-content" className="text-slate-800 dark:text-[#E5E2DD]">Message *</Label>
              <Textarea
                id="msg-content"
                placeholder="Type your message..."
                value={newMessage.message}
                onChange={(e) => setNewMessage({ ...newMessage, message: e.target.value })}
                className="mt-1 bg-white dark:bg-[#0D0D0D] border-slate-200 dark:border-[#2A2A2A] text-slate-800 dark:text-[#E5E2DD] min-h-[150px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMessageDialog(false)} className="border-slate-200 dark:border-[#2A2A2A] text-slate-800 dark:text-[#E5E2DD]">
              Cancel
            </Button>
            <Button 
              onClick={handleSendMessage}
              className="bg-[#70170F] hover:bg-[#8B1D12] text-slate-800 dark:text-[#E5E2DD]"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Progress Log Entry Dialog - Simplified Mobile-Friendly */}
      <Dialog open={showProgressLogForm} onOpenChange={setShowProgressLogForm}>
        <DialogContent className="max-w-md max-h-[90vh] flex flex-col bg-slate-50 dark:bg-[#1A1A1A] border-slate-200 dark:border-[#2A2A2A]">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-slate-800 dark:text-[#E5E2DD] text-xl">📸 Quick Update</DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-[#8A8A8A]">
              {new Date().toLocaleString()}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 py-4 min-h-0">
            {/* Project Selector */}
            {rfps.length > 1 && (
              <div>
                <Label className="text-slate-800 dark:text-[#E5E2DD] text-sm">Project</Label>
                <Select 
                  value={progressLogForm.project_id || selectedProjectId || rfps[0]?.project_id}
                  onValueChange={(value) => {
                    setSelectedProjectId(value);
                    setProgressLogForm(prev => ({ ...prev, project_id: value }));
                  }}
                >
                  <SelectTrigger className="mt-1 bg-white dark:bg-[#0D0D0D] border-slate-200 dark:border-[#2A2A2A] text-slate-800 dark:text-[#E5E2DD]">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-50 dark:bg-[#1A1A1A] border-slate-200 dark:border-[#2A2A2A]">
                    {rfps.map(rfp => (
                      <SelectItem key={rfp.project_id} value={rfp.project_id} className="text-slate-800 dark:text-[#E5E2DD]">
                        {rfp.project_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Photo Upload - Prominent for mobile */}
            <div className="p-4 rounded-lg border-2 border-dashed border-[#70170F] bg-[#70170F]/10">
              <input
                type="file"
                id="photo-upload"
                accept="image/*"
                multiple
                capture="environment"
                className="hidden"
                onChange={handlePhotoUpload}
              />
              <label 
                htmlFor="photo-upload"
                className="flex flex-col items-center gap-2 cursor-pointer"
              >
                {uploadingPhotos ? (
                  <Loader2 className="w-10 h-10 text-[#70170F] animate-spin" />
                ) : (
                  <Camera className="w-10 h-10 text-[#70170F]" />
                )}
                <span className="text-slate-800 dark:text-[#E5E2DD] font-medium">
                  {uploadingPhotos ? 'Uploading...' : 'Tap to Add Photos'}
                </span>
                <span className="text-xs text-slate-400 dark:text-[#5A5A5A]">Take photo or select from gallery</span>
              </label>
            </div>
            
            {/* Photo Preview */}
            {progressLogForm.photos.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-slate-400 dark:text-[#5A5A5A]">{progressLogForm.photos.length} photo(s)</p>
                  {progressLogForm.gps_location && (
                    <span className="text-xs text-green-400 flex items-center gap-1">
                      <Navigation className="w-3 h-3" /> Location captured
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {progressLogForm.photos.map((url, idx) => (
                    <div key={idx} className="relative aspect-square rounded overflow-hidden">
                      <img src={`${BACKEND_URL}${url}`} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" />
                      <button
                        onClick={() => setProgressLogForm(prev => ({
                          ...prev,
                          photos: prev.photos.filter((_, i) => i !== idx),
                          photo_metadata: (prev.photo_metadata || []).filter((_, i) => i !== idx)
                        }))}
                        className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
                      >
                        <XCircle className="w-3 h-3 text-white" />
                      </button>
                      {/* Timestamp overlay */}
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5">
                        <p className="text-[9px] text-white/80 truncate">
                          {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Notes */}
            <div>
              <Label htmlFor="log-notes" className="text-slate-800 dark:text-[#E5E2DD] text-sm">Notes</Label>
              <Textarea
                id="log-notes"
                placeholder="What's happening on site today..."
                value={progressLogForm.notes}
                onChange={(e) => setProgressLogForm({ ...progressLogForm, notes: e.target.value })}
                className="mt-1 bg-white dark:bg-[#0D0D0D] border-slate-200 dark:border-[#2A2A2A] text-slate-800 dark:text-[#E5E2DD] min-h-[100px]"
              />
            </div>
            
            {/* File Upload */}
            <div>
              <input
                type="file"
                id="file-upload"
                multiple
                className="hidden"
                onChange={handleFileUpload}
              />
              <label 
                htmlFor="file-upload"
                className="flex items-center gap-2 p-3 rounded-lg bg-white dark:bg-[#0D0D0D] border border-slate-200 dark:border-[#2A2A2A] cursor-pointer hover:border-[#70170F] transition-colors"
              >
                <Paperclip className="w-5 h-5 text-slate-500 dark:text-[#8A8A8A]" />
                <span className="text-sm text-slate-500 dark:text-[#8A8A8A]">Attach files (optional)</span>
              </label>
            </div>
            
            {/* Attached Files Preview */}
            {progressLogForm.attachments.length > 0 && (
              <div className="space-y-1">
                {progressLogForm.attachments.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-white dark:bg-[#0D0D0D] rounded text-sm">
                    <span className="text-slate-800 dark:text-[#E5E2DD] truncate">{file.name}</span>
                    <button
                      onClick={() => setProgressLogForm(prev => ({
                        ...prev,
                        attachments: prev.attachments.filter((_, i) => i !== idx)
                      }))}
                      className="text-red-400 hover:text-red-300"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* GPS Location Capture */}
            <button
              onClick={captureLocation}
              disabled={isCapturingLocation}
              className={`flex items-center gap-2 p-3 rounded-lg border w-full transition-colors ${
                progressLogForm.gps_location 
                  ? 'bg-green-900/20 border-green-700 text-green-400'
                  : 'bg-white dark:bg-[#0D0D0D] border-slate-200 dark:border-[#2A2A2A] text-slate-500 dark:text-[#8A8A8A] hover:border-[#70170F]'
              }`}
            >
              {isCapturingLocation ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Navigation className="w-5 h-5" />
              )}
              <span className="text-sm">
                {isCapturingLocation 
                  ? 'Getting location...' 
                  : progressLogForm.gps_location 
                    ? '✓ Location captured'
                    : 'Capture location (optional)'
                }
              </span>
            </button>
          </div>

          <DialogFooter className="flex-shrink-0 pt-4 border-t border-slate-200 dark:border-[#2A2A2A]">
            <Button variant="outline" onClick={() => setShowProgressLogForm(false)} className="border-slate-200 dark:border-[#2A2A2A] text-slate-800 dark:text-[#E5E2DD]">
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitProgressLog}
              className="bg-[#70170F] hover:bg-[#8B1D12] text-slate-800 dark:text-[#E5E2DD] flex-1"
            >
              <Send className="w-4 h-4 mr-2" />
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Controls Dialog */}
      <Dialog open={showAdminControls} onOpenChange={setShowAdminControls}>
        <DialogContent className="max-w-md bg-slate-50 dark:bg-[#1A1A1A] border-slate-200 dark:border-[#2A2A2A]">
          <DialogHeader>
            <DialogTitle className="text-slate-800 dark:text-[#E5E2DD] flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-500" />
              Portal Admin Controls
            </DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-[#8A8A8A]">
              Manage portal access and permissions for {contractor?.company_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Portal Access Status */}
            <div className="p-4 rounded-lg bg-white dark:bg-[#0D0D0D] border border-slate-200 dark:border-[#2A2A2A]">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-medium text-slate-800 dark:text-[#E5E2DD]">Portal Access</h4>
                  <p className="text-xs text-slate-400 dark:text-[#5A5A5A]">Enable or disable this contractor&apos;s portal access</p>
                </div>
                <Switch 
                  checked={contractor?.portal_enabled !== false} 
                  onCheckedChange={(checked) => {
                    toast.success(checked ? 'Portal access enabled' : 'Portal access disabled');
                  }}
                />
              </div>
            </div>

            {/* Permissions */}
            <div className="p-4 rounded-lg bg-white dark:bg-[#0D0D0D] border border-slate-200 dark:border-[#2A2A2A]">
              <h4 className="font-medium text-slate-800 dark:text-[#E5E2DD] mb-3">Permissions</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500 dark:text-[#8A8A8A]">Can view project documents</span>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500 dark:text-[#8A8A8A]">Can submit bids</span>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500 dark:text-[#8A8A8A]">Can view other contractor bids</span>
                  <Switch />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500 dark:text-[#8A8A8A]">Can download attachments</span>
                  <Switch defaultChecked />
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="p-4 rounded-lg bg-white dark:bg-[#0D0D0D] border border-slate-200 dark:border-[#2A2A2A]">
              <h4 className="font-medium text-slate-800 dark:text-[#E5E2DD] mb-3">Quick Actions</h4>
              <div className="space-y-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full justify-start border-slate-200 dark:border-[#2A2A2A] text-slate-800 dark:text-[#E5E2DD]"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/contractor-portal?email=${contractor?.email}`);
                    toast.success('Portal link copied to clipboard');
                  }}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Copy Portal Link
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full justify-start border-slate-200 dark:border-[#2A2A2A] text-slate-800 dark:text-[#E5E2DD]"
                  onClick={() => navigate(`/contractors`)}
                >
                  <User className="w-4 h-4 mr-2" />
                  View Contractor Profile
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full justify-start border-red-800 text-red-400 hover:bg-red-900/20"
                  onClick={() => {
                    toast.error('Portal access revoked');
                    setShowAdminControls(false);
                  }}
                >
                  <Ban className="w-4 h-4 mr-2" />
                  Revoke All Access
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdminControls(false)} className="border-slate-200 dark:border-[#2A2A2A] text-slate-800 dark:text-[#E5E2DD]">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <GlobalFooter tenantName={tenantCompanyName} darkMode={true} />
    </div>
  );
}
